import { useAnketaI18n } from "src/internationalization";
import { AutonubilApplianceFlow } from "./appliance-flow";

/*
 * "How opsanio works" - the signature appliance data-flow picture: every source is
 * connected via its native protocol, aggregated into ONE appliance in your data
 * center, then served back as five products. Replaces the former static
 * Collect→Merge→Enrich→Unify card grid with the animated diagram from the product
 * presentation. Framed on a navy "stage" (theme-independent) so it keeps the
 * presentation's dark look in both light and dark page themes; all motion is
 * reduced-motion-gated (see homepage-theme.css). Themed via --ak-* tokens.
 */

const tPrefix = "anketa.autonubil.home.how";

export const AutonubilHowItWorks = (): JSX.Element => {
  const { translate } = useAnketaI18n();

  return (
    <div
      style={{
        background: "var(--ak-page-bg)",
        padding: "72px clamp(24px, 5vw, 48px)",
        borderTop: "1px solid var(--ak-border)",
      }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              fontSize: "0.7rem",
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              color: "var(--ak-accent)",
              marginBottom: 10,
            }}
          >
            {translate(`${tPrefix}.eyebrow`, { defaultMessage: "How opsanio works" } as any)}
          </div>
          <div style={{ width: "fit-content", maxWidth: "100%", margin: "0 auto 16px" }}>
            <h2
              style={{
                color: "var(--ak-text)",
                fontWeight: 800,
                letterSpacing: "-0.02em",
                fontSize: "clamp(1.75rem, 4vw, 2.5rem)",
                margin: 0,
                textWrap: "balance",
              }}
            >
              {translate(`${tPrefix}.title`, {
                defaultMessage: "From raw data to a sovereign IT picture",
              } as any)}
            </h2>
            <div
              style={{
                width: "100%",
                height: 3,
                marginTop: 14,
                background: "linear-gradient(90deg, var(--ak-accent) 0%, var(--ak-accent-2) 100%)",
                borderRadius: 2,
              }}
            />
          </div>
          <p
            style={{
              maxWidth: 640,
              margin: "0 auto",
              color: "var(--ak-text-secondary)",
              fontSize: "clamp(0.97rem, 1.6vw, 1.1rem)",
              lineHeight: 1.65,
            }}
          >
            {translate(`${tPrefix}.subtitle`, {
              defaultMessage:
                "Every source is connected via its native protocol - firewalls, network, servers, virtualisation, identity and cloud - continuously aggregated into one appliance in your data center, then served back as five products.",
            } as any)}
          </p>
        </div>

        {/* navy stage that frames the diagram - always dark, works in both themes */}
        <div
          style={{
            position: "relative",
            borderRadius: 20,
            padding: "clamp(16px, 3vw, 30px)",
            background: "var(--ak-hero-bg)",
            border: "1px solid var(--ak-on-navy-border)",
            boxShadow: "0 12px 40px var(--ak-shadow-strong)",
            overflow: "hidden",
          }}
        >
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              pointerEvents: "none",
              backgroundImage:
                "linear-gradient(var(--ak-hero-grid) 1px, transparent 1px), linear-gradient(90deg, var(--ak-hero-grid) 1px, transparent 1px)",
              backgroundSize: "44px 44px",
              WebkitMaskImage: "radial-gradient(70% 60% at 50% 45%, #000, transparent 82%)",
              maskImage: "radial-gradient(70% 60% at 50% 45%, #000, transparent 82%)",
            }}
          />
          <div style={{ position: "relative" }}>
            <AutonubilApplianceFlow />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutonubilHowItWorks;
